import type { Erc20Transfer, LowerHex } from '../../types'
import type { InternalTransactionWithIndex } from '../../types/internalTransaction'
import type { TransactionIndex } from '../../types/transactionIndex'
import { createInternalTransactionId } from '../../utils/createInternalTransactionId'
import type {
  ContractCreation,
  Explorer,
  HeadersInput,
  LogWithBlockNumber,
} from '../types'
import { createKaiascanClient } from './client'
import { getBlockNumberByTimestamp } from './getBlockNumberByTimestamp'
import { getContractCreation } from './getContractCreation'
import { getContractSourceCodeForAVerifiedContract } from './getContractSourceCodeForAVerifiedContract'
import { getEventLogsByAddressAndTopic } from './getEventLogsByAddressAndTopic'
import { getInternalTransactionsByAddress } from './getInternalTransactionsByAddress'
import { getInternalTransactionsByTransactionHash } from './getInternalTransactionsByTransactionHash'
import { getTokenTransferEventsByAddress } from './getTokenTransferEventsByAddress'
import { getTransactionInfo } from './getTransactionInfo'
import { getTransactionsByAddress } from './getTransactionsByAddress'
import { createKaiascanOapiClient } from './oapiClient'

type CreateKaiascanParameters = {
  name: string
  baseUrl: string
  apiBaseUrl?: string
  oapiBaseUrl?: string
  apiKey?: string
  proxyUrl?: string
  pageSize?: number
}

/**
 * headers input を request ごとの headers に解決する。
 *
 * @param headers - 静的 headers または非同期 headers factory。
 * @returns request に渡す headers。
 */
const resolveHeaders = async (
  headers: HeadersInput,
): Promise<Record<string, string> | undefined> => {
  if (headers === undefined) {
    return undefined
  }
  if (typeof headers === 'function') {
    return await headers()
  }
  return headers
}

/**
 * Kaiascan explorer adapter を作成する。
 *
 * @param parameters - explorer 名、page URL、API URL、API key、proxy URL、page size。
 * @returns Explorer interface に準拠した Kaiascan adapter。
 */
export const createKaiascan = ({
  name,
  baseUrl,
  apiBaseUrl,
  oapiBaseUrl,
  apiKey,
  proxyUrl,
  pageSize = 500,
}: CreateKaiascanParameters): Explorer => {
  const client = createKaiascanClient({
    apiBaseUrl,
    apiKey,
    proxyUrl,
  })
  const oapiClient = createKaiascanOapiClient({
    oapiBaseUrl,
    apiKey,
    proxyUrl,
  })

  return {
    name,
    baseUrl,
    getAddressTransactionIndexes: async ({
      address,
      fromBlock,
      toBlock,
      headers,
    }) => {
      const indexes = new Map<LowerHex, TransactionIndex>()
      let page = 1
      while (true) {
        const resolvedHeaders = await resolveHeaders(headers)
        const result = await getTransactionsByAddress({
          client,
          address,
          startblock: fromBlock,
          endblock: toBlock,
          page,
          offset: pageSize,
          headers: resolvedHeaders,
        })
        for (const index of result) {
          if (!indexes.has(index.hash)) {
            indexes.set(index.hash, index)
          }
        }
        if (result.length !== pageSize) {
          break
        }
        page += 1
      }
      return [...indexes.values()]
    },
    getAddressInternalTransactions: async ({
      address,
      fromBlock,
      toBlock,
      headers,
    }) => {
      const internalTransactions = new Map<
        string,
        InternalTransactionWithIndex
      >()
      let page = 1
      while (true) {
        const resolvedHeaders = await resolveHeaders(headers)
        const result = await getInternalTransactionsByAddress({
          client,
          address,
          startblock: fromBlock,
          endblock: toBlock,
          page,
          offset: pageSize,
          headers: resolvedHeaders,
        })
        for (const transaction of result) {
          if (transaction.isRoot) {
            continue
          }
          const id = createInternalTransactionId(transaction)
          if (!internalTransactions.has(id)) {
            internalTransactions.set(id, transaction)
          }
        }
        if (result.length !== pageSize) {
          break
        }
        page += 1
      }
      return [...internalTransactions.values()]
    },
    getAddressTokenTransferIndexes: async ({
      address,
      fromBlock,
      toBlock,
      headers,
    }) => {
      const indexes = new Map<LowerHex, TransactionIndex>()
      const erc20Transfers: Erc20Transfer[] = []
      let page = 1
      while (true) {
        const resolvedHeaders = await resolveHeaders(headers)
        const result = await getTokenTransferEventsByAddress({
          client,
          address,
          startblock: fromBlock,
          endblock: toBlock,
          page,
          offset: pageSize,
          headers: resolvedHeaders,
        })
        for (const index of result.indexes) {
          if (!indexes.has(index.hash)) {
            indexes.set(index.hash, index)
          }
        }
        erc20Transfers.push(...result.erc20Transfers)
        if (result.indexes.length !== pageSize) {
          break
        }
        page += 1
      }
      return {
        indexes: [...indexes.values()],
        erc20Transfers,
      }
    },
    getBlockNumberOfTimestamp: async ({ timestamp, headers }) => {
      const resolvedHeaders = await resolveHeaders(headers)
      return getBlockNumberByTimestamp({
        client,
        timestamp: Math.floor(timestamp / 1000),
        headers: resolvedHeaders,
      })
    },
    getContract: async ({ address, headers }) => {
      const resolvedHeaders = await resolveHeaders(headers)
      return getContractSourceCodeForAVerifiedContract({
        client,
        address,
        headers: resolvedHeaders,
      })
    },
    getContractCreations: async ({ addresses, headers }) => {
      const creations: ContractCreation[] = []
      for (const address of addresses) {
        const resolvedHeaders = await resolveHeaders(headers)
        const result = await getContractCreation({
          client,
          address,
          headers: resolvedHeaders,
        })
        creations.push(...result)
      }
      return creations
    },
    getEventLogs: async ({ address, topic0, fromBlock, toBlock, headers }) => {
      const logs: LogWithBlockNumber[] = []
      let page = 1
      while (true) {
        const resolvedHeaders = await resolveHeaders(headers)
        const result = await getEventLogsByAddressAndTopic({
          client: oapiClient,
          address,
          topic0,
          fromBlock: fromBlock ?? 0,
          toBlock,
          page,
          size: pageSize,
          headers: resolvedHeaders,
        })
        logs.push(...result)
        if (result.length !== pageSize) {
          break
        }
        page += 1
      }
      return logs
    },
    getInternalTransactionOfTransaction: async ({ hash, headers }) => {
      const resolvedHeaders = await resolveHeaders(headers)
      const transactionInfo = await getTransactionInfo({
        client,
        txhash: hash,
        headers: resolvedHeaders,
      })
      return getInternalTransactionsByTransactionHash({
        client: oapiClient,
        txhash: hash,
        transactionInfo,
        headers: resolvedHeaders,
      })
    },
  }
}
